type AnyRecord = Record<string, any>;

const toText = (value: unknown): string => (value == null ? '' : String(value)).trim();
const isBayLikeItem = (item: AnyRecord): boolean => {
  const rows = Array.isArray(item.summaryRows) ? item.summaryRows : [];
  const hasBayLabel = rows.some((r: any) => /tangent width|facet widths|joint angles/i.test(String(r?.label || '')));
  return hasBayLabel || !!toText(item.bayTangentWidthMm || item.bayFacetWidthsMm || item.bayJointAnglesDeg);
};

const sizeTextFor = (item: AnyRecord): string => {
  if (!item.widthMm && !item.heightMm) return '';
  return `${item.widthMm || ''}${(item.widthMm && item.heightMm) ? ' × ' : ''}${item.heightMm || ''} mm`;
};

const kvRow = (label: string, value: unknown): string => {
  const v = toText(value);
  if (!v) return '';
  return `
    <tr>
      <td style="padding:2px 8px 2px 0; width:50%; white-space:nowrap; vertical-align:top; color:#334155; font-weight:600;">${label}</td>
      <td style="padding:2px 0; width:50%; vertical-align:top; color:#0f172a;">${v}</td>
    </tr>
  `;
};

const buildKvTableFromSummaryRows = (item: AnyRecord): string => {
  const rows = Array.isArray(item.summaryRows) ? item.summaryRows : [];
  if (!rows.length) return '';
  const rowsHtml = rows
    .map((r: any) => kvRow(toText(r?.label), r?.value))
    .filter(Boolean)
    .join('');
  if (!rowsHtml) return '';
  return `
    <table role="presentation" style="width:100%; border-collapse:collapse; font-size:12px; line-height:1.25;">
      <tbody>
        ${rowsHtml}
        ${kvRow('Qty', item.quantity)}
      </tbody>
    </table>
  `;
};

const buildKvTable = (item: AnyRecord): string => {
  const fromSummaryRows = buildKvTableFromSummaryRows(item);
  if (fromSummaryRows) return fromSummaryRows;
  const sizeText = sizeTextFor(item);
  const colourValue = toText(item.colour) || [toText(item.externalColour), toText(item.internalColour)].filter(Boolean).join(' / ');
  return `
    <table role="presentation" style="width:100%; border-collapse:collapse; font-size:12px; line-height:1.25;">
      <tbody>
        ${kvRow('Window type', item.typeName)}
        ${kvRow('Material', item.materialName)}
        ${kvRow('Glass type', item.glassType)}
        ${kvRow('Style', item.styleName)}
        ${kvRow('Reference', item.reference)}
        ${kvRow('Size', sizeText)}
        ${kvRow('Variant', item.variant)}
        ${kvRow('Beading type', item.beadingType)}
        ${kvRow('Colour / finish', colourValue)}
        ${kvRow('Handle colour', item.handleColour)}
        ${kvRow('Handle style', item.handleStyle)}
        ${kvRow('Hinge type', item.hingeType)}
        ${kvRow('Extenders (mm)', item.extenders)}
        ${kvRow('Georgian', item.georgian)}
        ${kvRow('Astragal', item.astragal)}
        ${kvRow('Astragal bar side', item.astragalBarSide)}
        ${kvRow('Hinge placement', item.hingePlacement)}
        ${kvRow('Open direction', item.openDirection)}
        ${kvRow('Trickle vents', item.trickleVents)}
        ${kvRow('Cill', item.cill)}
        ${kvRow('Notes', item.notes)}
        ${kvRow('Qty', item.quantity)}
      </tbody>
    </table>
  `;
};

const buildVisualDesignerItemsHtml = (items: AnyRecord[]): string => {
  const TILE_AR_W = 8;
  const TILE_AR_H = 7;
  const tileWH = (w: number) => {
    const width = Math.max(1, Math.round(w));
    const height = Math.max(1, Math.round((width * TILE_AR_H) / TILE_AR_W));
    return { width, height };
  };
  const tile1 = tileWH(320);

  const rowsHtml = (items || []).map((item: AnyRecord, idx: number) => `
    ${idx > 0 ? `
    <tr class="vd-page-break-row">
      <td colspan="2" style="padding:0; border:0; height:0;">
        <div style="page-break-before:always; break-before:page; height:0; line-height:0;"></div>
      </td>
    </tr>
    ` : ''}
    <tr class="vd-item-row">
      <td style="width:50%; padding:8px; vertical-align:top;">
        ${item.imageUrl ? `
          <div style="width:${tile1.width}px; height:${tile1.height}px; background:#f1f5f9; border:1px solid #e2e8f0; border-radius:6px; overflow:hidden; display:flex; align-items:center; justify-content:center;">
            <img src="${item.imageUrl}" alt="${item.name}" style="width:100%; height:100%; object-fit:contain; display:block;" />
          </div>
        ` : ''}
      </td>
      <td style="width:50%; padding:8px; vertical-align:top;">
        <div style="font-weight:700; font-size:13px; margin:0 0 4px 0; color:#0f172a;">${item.name}</div>
        ${buildKvTable(item)}
      </td>
    </tr>
  `).join('');

  return `
    <table class="visual-designer-table" role="presentation" style="width:100%; border-collapse:collapse; table-layout:fixed; margin:10px 0;">
      <tbody>
        ${rowsHtml}
      </tbody>
    </table>
  `;
};

const buildVisualDesignerItemsV2Html = (items: AnyRecord[]): string => {
  const TILE_HEIGHT_FOUR_UP = 132;
  const TILE_HEIGHT_TWO_UP = 138;

  const rowsHtml = (items || []).map((item: AnyRecord, idx: number) => {
    const isUploadItem = String(item.source || '').trim().toLowerCase() === 'upload';
    const presExt = toText(item.imageUrlPresentationExternal || item.imageUrlExternal || (isUploadItem ? '' : item.imageUrl));
    const presInt = toText(item.imageUrlPresentationInternal || item.imageUrlInternal);
    const desExt = toText(item.imageUrlDesignExternal);
    const desInt = toText(item.imageUrlDesignInternal);
    const baySchematic = isBayLikeItem(item) ? toText(item.imageUrlBaySchematic) : '';
    const imageSlots = [
      { label: 'Presentation · Outside', src: presExt },
      { label: 'Presentation · Inside', src: presInt },
      { label: 'Design · Outside', src: desExt },
      { label: 'Design · Inside', src: desInt },
    ].filter((slot) => !!slot.src);
    const tileHeight = imageSlots.length > 2 ? TILE_HEIGHT_FOUR_UP : TILE_HEIGHT_TWO_UP;
    const imageRows = imageSlots.reduce<string[]>((acc, slot, slotIdx) => {
      const rowIdx = Math.floor(slotIdx / 2);
      const cell = `
            <td style="width:50%; padding:${rowIdx === 0 ? '0' : '6px'} ${slotIdx % 2 === 0 ? '4px' : '0'} 0 ${slotIdx % 2 === 0 ? '0' : '4px'}; vertical-align:top;">
              <div style="width:100%; height:${tileHeight}px; background:#d9dee7; border:1px solid #cbd3df; border-radius:6px; overflow:hidden; display:flex; align-items:center; justify-content:center;">
                <img src="${slot.src}" alt="${item.name} (${slot.label})" style="width:100%; height:100%; object-fit:contain; display:block;" />
              </div>
              <div style="font-size:10px; color:#64748b; text-align:center; margin-top:4px;">${slot.label}</div>
            </td>`;
      acc[rowIdx] = `${acc[rowIdx] || ''}${cell}`;
      return acc;
    }, []);

    const imageBlock = `
        <table role="presentation" style="width:100%; border-collapse:collapse; table-layout:fixed;">
          ${imageRows.map((row) => `<tr>${row}</tr>`).join('')}
          ${baySchematic ? `
          <tr>
            <td colspan="2" style="padding:6px 0 0 0; vertical-align:top;">
              <div style="width:100%; margin:0 auto; background:#d9dee7; border:1px solid #cbd3df; border-radius:6px; overflow:hidden;">
                <img src="${baySchematic}" alt="${item.name} (bay schematic)" style="width:100%; height:auto; display:block;" />
              </div>
              <div style="font-size:10px; color:#64748b; text-align:center; margin-top:4px;">Bay Schematic</div>
            </td>
          </tr>
          ` : ''}
        </table>
      `;

    return `
      ${idx > 0 ? `
      <tr class="vd-page-break-row">
        <td colspan="2" style="padding:0; border:0; height:0;">
          <div style="page-break-before:always; break-before:page; height:0; line-height:0;"></div>
        </td>
      </tr>
      ` : ''}
      <tr class="vd-item-row">
        <td style="width:50%; padding:8px; vertical-align:top;">
          ${imageBlock}
        </td>
        <td style="width:50%; padding:8px; vertical-align:top;">
          <div style="font-weight:700; font-size:13px; margin:0 0 4px 0; color:#0f172a;">${item.name}</div>
          ${buildKvTable(item)}
        </td>
      </tr>
    `;
  }).join('');

  return `
    <table class="visual-designer-table-v2" role="presentation" style="width:100%; border-collapse:collapse; table-layout:fixed; margin:10px 0;">
      <colgroup>
        <col style="width:50%;" />
        <col style="width:50%;" />
      </colgroup>
      <tbody>
        ${rowsHtml}
      </tbody>
    </table>
  `;
};

export function applyVisualDesignerTemplateBlocks(renderedTemplate: string, templateData: AnyRecord): string {
  let result = String(renderedTemplate || '');

  if (templateData.hasVisualDesignerItems) {
    const vdHtml = buildVisualDesignerItemsHtml(templateData.visualDesignerItems || []);
    result = result.replace(/\{\{#visualDesignerItems\}([\s\S]*?)\{\{\/visualDesignerItems\}\}/g, vdHtml);
  }

  if (templateData.hasVisualDesignerItemsV2) {
    const vdHtmlV2 = buildVisualDesignerItemsV2Html(templateData.visualDesignerItemsV2 || []);
    result = result.replace(/\{\{#visualDesignerItemsV2\}([\s\S]*?)\{\{\/visualDesignerItemsV2\}\}/g, vdHtmlV2);
  }

  return result;
}
